/**
 * ChatWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Unified with all other widgets in the platform for consistent 50% width layout.
 *
 * Shows chat session details with message history.
 */

import { useState } from 'react';
import { MessageSquare, Clock, Edit3, Trash2, Send } from 'lucide-react';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import type { ChatSession, ChatMessage } from '../rows/ChatRow';

// =============================================================================
// TYPES
// =============================================================================

interface ChatWidgetProps {
  chatId?: string;
  chat?: ChatSession;
  onClose: () => void;
  onSave?: (data: ChatFormData) => void;
  onSendMessage?: (text: string) => void;
  onDelete?: () => void;
}

export interface ChatFormData {
  title: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ChatWidget({
  chatId,
  chat,
  onClose,
  onSave,
  onSendMessage,
  onDelete,
}: ChatWidgetProps) {
  const { t, language } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(chat?.title || '');
  const [newMessage, setNewMessage] = useState('');

  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Use provided chat or create empty for new
  const currentChat = chat || {
    id: chatId || '',
    title: t('chatWidget.newChat'),
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleSaveTitle = () => {
    if (editedTitle.trim() && onSave) {
      onSave({ title: editedTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && onSendMessage) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  const formatDateTime = (isoDate: string) => {
    return new Date(isoDate).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const formatTime = (isoDate: string) => {
    return new Date(isoDate).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DetailPanel
      title={currentChat.title}
      subtitle={`${currentChat.messages.length} ${t('chatWidget.messages')}`}
      icon={<MessageSquare size={20} style={{ color: '#8b5cf6' }} />}
      onClose={onClose}
      accentColor="#8b5cf6"
      width="50%"
    >
      {/* Title Edit */}
      <DetailSection title={t('chatWidget.conversationTitle')}>
        {isEditingTitle ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
              autoFocus
              style={{
                flex: 1,
                padding: '0.625rem',
                border: '1px solid var(--brand)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            />
            <button
              onClick={handleSaveTitle}
              style={{
                padding: '0.625rem 1rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: 'var(--brand)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {t('chatWidget.save')}
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              background: 'var(--bg)',
              borderRadius: '0.5rem',
            }}
          >
            <span style={{ fontWeight: 500 }}>{currentChat.title}</span>
            {onSave && (
              <button
                onClick={() => {
                  setEditedTitle(currentChat.title);
                  setIsEditingTitle(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.25rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                }}
              >
                <Edit3 size={14} />
              </button>
            )}
          </div>
        )}
      </DetailSection>

      {/* Metadata */}
      <DetailSection title={t('chatWidget.information')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <Clock size={14} />
            <span>{t('chatWidget.created')} {formatDateTime(currentChat.createdAt)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <Clock size={14} />
            <span>{t('chatWidget.lastUpdate')} {formatDateTime(currentChat.updatedAt)}</span>
          </div>
        </div>
      </DetailSection>

      {/* Message History */}
      <DetailSection title={t('chatWidget.messageHistory')}>
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {currentChat.messages.length === 0 ? (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
              }}
            >
              {t('chatWidget.noMessages')}
            </div>
          ) : (
            currentChat.messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '0.75rem 1rem',
                    borderRadius: '1rem',
                    background: msg.role === 'user' ? '#8b5cf6' : 'var(--bg)',
                    color: msg.role === 'user' ? 'white' : 'var(--text)',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  {msg.content}
                </div>
                <span
                  style={{
                    fontSize: '0.6875rem',
                    color: 'var(--text-muted)',
                    marginTop: '0.25rem',
                    paddingLeft: msg.role === 'user' ? 0 : '0.5rem',
                    paddingRight: msg.role === 'user' ? '0.5rem' : 0,
                  }}
                >
                  {msg.role === 'user' ? t('chatWidget.you') : t('chatWidget.assistant')}
                </span>
              </div>
            ))
          )}
        </div>
      </DetailSection>

      {/* New Message */}
      {onSendMessage && (
        <DetailSection title={t('chatWidget.newMessage')}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={t('chatWidget.writeMessage')}
              rows={3}
              style={{
                flex: 1,
                padding: '0.625rem',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                resize: 'vertical',
              }}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: newMessage.trim() ? '#8b5cf6' : 'var(--line)',
              color: newMessage.trim() ? 'white' : 'var(--text-muted)',
              cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 500,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <Send size={14} />
            {t('chatWidget.send')}
          </button>
        </DetailSection>
      )}

      {/* Actions */}
      <DetailActions>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            style={{
              padding: '0.625rem 1.25rem',
              border: '1px solid #ef4444',
              borderRadius: '0.375rem',
              background: 'white',
              color: '#ef4444',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <Trash2 size={14} />
            {t('chatWidget.delete')}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '0.625rem 1.25rem',
            border: '1px solid var(--line)',
            borderRadius: '0.375rem',
            background: 'white',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          {t('chatWidget.close')}
        </button>
      </DetailActions>
    </DetailPanel>
  );
}
